import React, { useState } from 'react'
import { type NurseryItemData } from '../nureseries/type'
import { nurseryNameWithPrefecture } from '../utils'

interface NurseryMarkerProps {
  nursery: NurseryItemData
  isActive?: boolean
  onClick?: (nursery: NurseryItemData, position: { x: number; y: number }) => void
  onHover?: (nursery: NurseryItemData | null) => void
}

export const NurseryMarker: React.FC<NurseryMarkerProps> = ({ nursery, isActive = false, onClick, onHover }) => {
  const [isHovered, setIsHovered] = useState(false)

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onClick) {
      // Get the marker's screen position
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
      const position = {
        x: rect.left + rect.width / 2, // center of pin
        y: rect.top, // top of pin
      }
      onClick(nursery, position)
    }
  }

  const handleMouseEnter = () => {
    setIsHovered(true)
    if (onHover) {
      onHover(nursery)
    }
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    if (onHover) {
      onHover(null)
    }
  }

  return (
    <div
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        transform: 'translate(-50%, -100%)',
        zIndex: isActive || isHovered ? 1000 : 1,
      }}
      className="marker-container absolute"
    >
      <PinIcon isActive={isActive || isHovered} />
      {isHovered && !isActive && (
        <div
          className="absolute bg-white rounded-lg shadow-lg p-2 whitespace-nowrap"
          style={{
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '8px',
            zIndex: 1001,
          }}
        >
          <div className="text-sm font-bold text-gray-800">{nursery.name}</div>
          <div className="text-xs text-gray-600">{nurseryNameWithPrefecture(nursery)}</div>
          {/* Arrow pointing down */}
          <div
            className="absolute bg-white"
            style={{
              bottom: '-4px',
              left: '50%',
              transform: 'translateX(-50%) rotate(45deg)',
              width: '8px',
              height: '8px',
            }}
          />
        </div>
      )}
    </div>
  )
}

const PinIcon: React.FC<{ isActive?: boolean }> = ({ isActive = false }) => {
  const circleFillColor = isActive ? '#DC2626' : '#189855'
  const outerFillColor = 'white'

  return (
    <svg
      width="30"
      height="34"
      viewBox="0 0 30 34"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`transition-transform duration-200 ${isActive ? 'scale-125' : 'scale-100'} cursor-pointer`}
      style={{
        filter: isActive
          ? 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.6)) drop-shadow(0 4px 8px rgba(0, 0, 0, 0.4))'
          : 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.4)) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
      }}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15 0C23.2843 0 30 6.71573 30 15C30 22.2574 24.846 28.311 17.9986 29.7002L15.466 33.3268C15.4135 33.4024 15.3437 33.464 15.2626 33.5066C15.1815 33.5492 15.0914 33.5714 15 33.5714C14.9086 33.5714 14.8185 33.5492 14.7374 33.5066C14.6563 33.464 14.5865 33.4024 14.534 33.3268L12.0014 29.7002C5.15398 28.311 0 22.2574 0 15C0 6.71573 6.71573 0 15 0Z"
        fill={outerFillColor}
      />
      <path
        d="M27.8549 14.9997C27.8549 7.89892 22.0986 2.14258 14.9978 2.14258C7.89696 2.14258 2.14062 7.89892 2.14062 14.9997C2.14062 22.1005 7.89696 27.8569 14.9978 27.8569C22.0986 27.8569 27.8549 22.1005 27.8549 14.9997Z"
        fill={circleFillColor}
      />
      <path d="M10 21.7563V10" stroke="white" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="square" />
      <path
        d="M20 16.2609H10V10H20L16.8696 13.1304L20 16.2609Z"
        fill="white"
        stroke="white"
        strokeWidth="2"
        strokeMiterlimit="10"
        strokeLinecap="round"
      />
    </svg>
  )
}
